import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CdkObserveContent } from '@angular/cdk/observers';
import { NgStyle } from '@angular/common';
import {
  Component,
  ElementRef,
  Renderer2,
  Signal,
  ViewChild,
  computed,
  inject,
} from '@angular/core';
import { MatChip } from '@angular/material/chips';
import { MatDivider } from '@angular/material/divider';
import { PmIconComponent } from '@libs/ui';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { AppliedFilterChipsService } from 'demand-forecasting/services/applied-filter-chips.service';
import {
  selectAppliedFilters,
  selectAvailableFilters,
} from 'demand-forecasting/store/filter/filter.selectors';
import { LucideChevronDown } from '@lucide/angular';

@Component({
  selector: 'pm-forecast-applied-filters',
  templateUrl: './forecast-applied-filters.component.html',
  animations: [
    trigger('heightChange', [
      state(
        'closed',
        style({
          height: 'var(--closed-height-applied-filter-chips, 72px)',
        }),
      ),
      state(
        'open',
        style({
          height: '*',
        }),
      ),
      transition('closed <=> open', animate('300ms ease-in')),
    ]),
    trigger('fadeAnimation', [
      state('*', style({ opacity: 1 })),
      transition(':enter', [style({ opacity: 0 }), animate('300ms ease-in')]),
      transition(':leave', [animate('200ms ease-out', style({ opacity: 0 }))]),
    ]),
  ],
  imports: [
    MatDivider,
    NgStyle,
    MatChip,
    CdkObserveContent,
    TranslateModule,
    PmIconComponent,
  ],
})
export class ForecastAppliedFiltersComponent {
  private readonly renderer = inject(Renderer2);

  readonly GAP_BETWEEN_CHIPS = 8;
  readonly NUM_OF_PREVIEW_ROWS = 2;
  readonly CHIP_HEIGHT = 32;

  protected readonly dropdownIcon = LucideChevronDown;

  readonly MAX_NUM_OF_SHOWN_APPLIED_FILTERS = 60;
  public appliedFilterChipsService = inject(AppliedFilterChipsService);
  private readonly store = inject(Store);

  @ViewChild('appliedFilterChipsWrapper')
  appliedFilterChipsWrapper: ElementRef<HTMLDivElement>;
  @ViewChild('calcHeightElement')
  calcHeightElement: ElementRef<HTMLDivElement>;

  @ViewChild('collapseBar')
  collapseBar: ElementRef<HTMLDivElement> | undefined;

  isOpened: boolean;
  toggleNeeded: boolean;
  currentClosedHeight: number;

  appliedFilters = this.store.selectSignal(selectAppliedFilters);
  availableFilters = this.store.selectSignal(selectAvailableFilters);
  shownAppliedFilters = computed(() => {
    const filters = this.appliedFilterChipsService
      .appliedFilterChips()
      .slice(0, this.MAX_NUM_OF_SHOWN_APPLIED_FILTERS);
    return filters;
  });
  leftOverFilters: Signal<number> = computed(() => {
    const appliedFilters = this.appliedFilterChipsService.appliedFilterChips();
    return appliedFilters.length - this.MAX_NUM_OF_SHOWN_APPLIED_FILTERS > 0
      ? appliedFilters.length - this.MAX_NUM_OF_SHOWN_APPLIED_FILTERS
      : 0;
  });
  showChipIsAvailable: Signal<boolean> = computed(
    () => this.appliedFilterChipsService.appliedFilterChips().length > 0,
  );

  constructor() {
    this.isOpened = false;
    this.toggleNeeded = false;
  }

  chipsChanged() {
    this.renderer.setStyle(
      this.calcHeightElement.nativeElement,
      'height',
      'auto',
    );
    this.renderer.setStyle(
      this.calcHeightElement.nativeElement,
      'overflow',
      'visible',
    );

    const newOpenedHeight = this.calcHeightElement.nativeElement.offsetHeight;

    this.renderer.setStyle(
      this.calcHeightElement.nativeElement,
      'height',
      '0px',
    );
    this.renderer.setStyle(
      this.calcHeightElement.nativeElement,
      'overflow',
      'hidden',
    );

    const closedHeight = this.calcClosedHeight(newOpenedHeight);

    document.documentElement.style.setProperty(
      '--closed-height-applied-filter-chips',
      `${closedHeight}px`,
    );

    this.toggleNeeded = newOpenedHeight > closedHeight;
  }

  toggleIsOpened() {
    this.isOpened = !this.isOpened;
  }

  private calcClosedHeight(openedHeight: number): number {
    const possibleInitialHeight =
      this.NUM_OF_PREVIEW_ROWS * this.CHIP_HEIGHT +
      (this.NUM_OF_PREVIEW_ROWS - 1 <= 0 ? 0 : this.NUM_OF_PREVIEW_ROWS - 1) *
        this.GAP_BETWEEN_CHIPS;

    if (possibleInitialHeight > openedHeight) {
      return openedHeight;
    }

    return possibleInitialHeight;
  }
}
